import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Truck, 
  Package, 
  Clock, 
  Shield, 
  ChevronRight,
  Mail,
  MapPin,
  CheckCircle2,
  Search,
  Target,
  Thermometer,
  Box,
  Zap,
  ArrowRight,
  Loader2,
  MapPinned,
  Calendar,
  History,
  Gem
} from "lucide-react";
import { toast } from "sonner";

export default function LandingPage() {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState("");

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) {
      setTrackingError("Bitte Sendungsnummer eingeben");
      return;
    }
    
    setTrackingLoading(true);
    setTrackingError("");
    setTrackingResult(null);
    
    try {
      const res = await axios.get(`${API}/track/${trackingNumber.trim()}`);
      setTrackingResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setTrackingError("Sendung nicht gefunden. Bitte prüfen Sie die Nummer.");
      } else {
        setTrackingError("Fehler bei der Abfrage. Bitte versuchen Sie es erneut.");
      }
    } finally {
      setTrackingLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case "DELIVERED": return "bg-green-500";
      case "IN_TRANSIT": return "bg-blue-500";
      case "PICKED_UP": return "bg-indigo-500";
      case "CANCELLED": 
      case "CUSTOMER_ABSENT": return "bg-red-500";
      case "REDELIVERY": return "bg-orange-500";
      default: return "bg-amber-500";
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <a href="#tracking" className="text-slate-300 hover:text-white transition">
                Sendungsverfolgung
              </a>
              <a href="#services" className="text-slate-300 hover:text-white transition">
                Services
              </a>
              <a href="#contact" className="text-slate-300 hover:text-white transition">
                Kontakt
              </a>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-slate-800" data-testid="login-btn">
                  Anmelden
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="register-btn">
                  Registrieren
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Tracking */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 lg:py-24">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1681514583222-0579e6835666?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80" 
            alt="Logistics" 
            className="w-full h-full object-cover opacity-15"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Hero Text */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" data-testid="hero-title">
                Express-Versand für
                <span className="text-blue-400"> Ihre Pakete</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Schnelle und zuverlässige Zustellung deutschlandweit. 
                Abholung bis 17 Uhr - Zustellung am nächsten Tag.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8">
                    Jetzt starten
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right - Tracking Box */}
            <div id="tracking" className="bg-white rounded-2xl p-6 shadow-2xl" data-testid="tracking-box">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Search className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">Sendungsverfolgung</h3>
                  <p className="text-sm text-slate-500">Ohne Anmeldung</p>
                </div>
              </div>
              
              <form onSubmit={handleTrack} className="space-y-4">
                <div>
                  <Input
                    placeholder="Sendungsnummer eingeben (z.B. DE-123456789)"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                    className="text-slate-900 h-12 text-base"
                    data-testid="tracking-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-base"
                  disabled={trackingLoading}
                  data-testid="tracking-submit"
                >
                  {trackingLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Search className="h-5 w-5 mr-2" />
                  )}
                  Sendung verfolgen
                </Button>
              </form>
              
              {trackingError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{trackingError}</p>
                </div>
              )}
              
              {trackingResult && (
                <div className="mt-4 space-y-4" data-testid="tracking-result">
                  {/* Status Header */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-500">Sendung</span>
                      <span className="font-mono font-bold text-slate-900">{trackingResult.order_number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(trackingResult.status)}`}></div>
                      <span className="font-medium text-slate-900">{trackingResult.status_label}</span>
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <MapPinned className="h-4 w-4" />
                      <span>{trackingResult.receiver_city || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="h-4 w-4" />
                      <span>{trackingResult.delivery_date ? new Date(trackingResult.delivery_date).toLocaleDateString('de-DE') : "—"}</span>
                    </div>
                  </div>
                  
                  {/* History Timeline */}
                  {trackingResult.history && trackingResult.history.length > 0 && (
                    <div className="border-t pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <History className="h-4 w-4 text-slate-500" />
                        <span className="text-sm font-medium text-slate-700">Sendungsverlauf</span>
                      </div>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {trackingResult.history.slice().reverse().map((h, idx) => (
                          <div key={idx} className="flex gap-3 text-sm">
                            <span className="text-slate-400 text-xs w-16 flex-shrink-0">
                              {h.timestamp ? new Date(h.timestamp).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : ""}
                            </span>
                            <span className="text-slate-600">{h.event}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities - Modern & Clean */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-testid="capabilities-title">
              Unsere Kernfähigkeiten
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Spezialisiert auf anspruchsvolle Logistiklösungen
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Waffenversand */}
            <div className="group text-center" data-testid="capability-waffen">
              <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                <Target className="h-10 w-10 text-slate-700" />
              </div>
              <h3 className="font-semibold text-xl text-slate-900 mb-2">Waffenversand</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Sicherer und gesetzeskonformer Transport von Jagd- & Sportwaffen
              </p>
            </div>
            
            {/* Blutproben */}
            <div className="group text-center" data-testid="capability-blutproben">
              <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                <Thermometer className="h-10 w-10 text-slate-700" />
              </div>
              <h3 className="font-semibold text-xl text-slate-900 mb-2">Blutproben</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Temperaturkontrollierter Versand medizinischer Proben
              </p>
            </div>
            
            {/* Valoren / Wertsendungen */}
            <div className="group text-center" data-testid="capability-valoren">
              <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                <Gem className="h-10 w-10 text-slate-700" />
              </div>
              <h3 className="font-semibold text-xl text-slate-900 mb-2">Valoren</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Diskreter und versicherter Transport von Wertsendungen
              </p>
            </div>
            
            {/* Sperrware */}
            <div className="group text-center" data-testid="capability-sperrware">
              <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                <Box className="h-10 w-10 text-slate-700" />
              </div>
              <h3 className="font-semibold text-xl text-slate-900 mb-2">Sperrware</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Zuverlässiger Transport von sperrigen und schweren Gütern
              </p>
            </div>
            
            {/* Sonderfahrten */}
            <div className="group text-center" data-testid="capability-sonderfahrten">
              <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl group-hover:-translate-y-1 transition-all duration-300">
                <Zap className="h-10 w-10 text-slate-700" />
              </div>
              <h3 className="font-semibold text-xl text-slate-900 mb-2">Sonderfahrten</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Express-Direktfahrten für zeitkritische Sendungen
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-testid="services-title">
              Unsere Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Flexible Versandoptionen für jeden Bedarf
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-4">
                  <Truck className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Standard Express</h3>
                <p className="text-slate-600 text-sm">
                  Abholung bis 17 Uhr, Zustellung bis 17 Uhr am nächsten Tag
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Express bis 12 Uhr</h3>
                <p className="text-slate-600 text-sm">
                  Garantierte Zustellung vor 12 Uhr mittags
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Express bis 10 Uhr</h3>
                <p className="text-slate-600 text-sm">
                  Frühzustellung vor 10 Uhr vormittags
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Samstag Zustellung</h3>
                <p className="text-slate-600 text-sm">
                  Zustellung auch am Wochenende möglich
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="about" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Warum SonderCargo.de?
              </h2>
              <div className="space-y-4">
                {[
                  "Deutschlandweites Netzwerk",
                  "Echtzeit-Sendungsverfolgung",
                  "Flexible Abholzeiten",
                  "Geschäftskunden-Pauschalpreise",
                  "Persönlicher Support",
                  "Kauf auf Rechnung für Business"
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/register">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                    Kostenlos registrieren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1601912552080-0fb89fd08042?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" 
                alt="Delivery" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Bereit für zuverlässigen Versand?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Registrieren Sie sich jetzt und profitieren Sie von unseren günstigen Konditionen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register?type=business">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Business Account
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-slate-600 text-slate-500 cursor-not-allowed opacity-60"
              disabled
            >
              Privatkonto (deaktiviert)
            </Button>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Kontakt
            </h2>
          </div>
          
          <div className="max-w-md mx-auto">
            <div className="text-center">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">E-Mail</h3>
              <p className="text-slate-600">sm-medical@outlook.de</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <Link to="/impressum" className="text-slate-400 hover:text-white text-sm transition">
                Impressum
              </Link>
              <span className="text-slate-400 text-sm">
                © 2026 SM-Medical UG (haftungsbeschränkt)
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
